import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardBody,
  Button,
  Spinner,
  Chip,
  Switch,
  Select,
  SelectItem,
} from "@heroui/react";
import {
  Image as ImageIcon,
  Trash,
  Check,
  CaretLeft,
  CaretRight,
  Tag,
} from "@phosphor-icons/react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import EntitySearchBar from "./EntitySearchBar";
import { Acquisition } from "shared/types/acquisition";

interface ImagePickerProps {
  imageName: string;
  selectedImageId?: string;
  onImageSelect: (sharePointFileId: string) => void;
  buildingId?: string;
  unitId?: string;
  acquisitionId?: string;
  /**
   * When true, the picker is rendered inside a loop iteration whose entity is
   * already determined (e.g., iteration 2 of `building.units`). The in-picker
   * building/unit/acquisition search bars and the context-filter toggle are
   * hidden so the user only sees images for the iteration's own entity.
   */
  isLoopScoped?: boolean;
  /**
   * Top-level (non-loop) usage: pre-populate the Gebouw search bar with the
   * building already chosen as document context, so the user sees that
   * building selected from the start instead of an empty search field.
   */
  prefilledBuilding?: any;
}

interface FileLog {
  id: number;
  sharePointFileId: string;
  fileName: string;
  imageUrl?: string;
  imageFound: boolean;
}

interface FileLabel {
  id: string;
  label: string;
}

const ImagePicker: React.FC<ImagePickerProps> = ({
  imageName,
  selectedImageId,
  onImageSelect,
  buildingId,
  unitId,
  acquisitionId,
  isLoopScoped = false,
  prefilledBuilding,
}) => {
  const { session } = useSession();
  const [images, setImages] = useState<FileLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const [useContextFilter, setUseContextFilter] = useState(true);
  const [availableLabels, setAvailableLabels] = useState<FileLabel[]>([]);
  const [selectedLabel, setSelectedLabel] = useState<string>("");
  const [isImagePickerVisible, setIsImagePickerVisible] = useState(false);
  const pageSize = 12;

  // Entity selection state - overrides context when selected. Seeded from
  // prefilledBuilding so the Gebouw bar shows the document-context building
  // on first render without requiring the user to re-select it.
  const [selectedBuilding, setSelectedBuilding] = useState<any>(
    prefilledBuilding ?? null,
  );
  // Track which prefilled building we already consumed so later context
  // changes refresh the picker's selection — but only while the user hasn't
  // manually overridden it.
  const lastPrefilledBuildingRef = React.useRef<any>(prefilledBuilding ?? null);
  useEffect(() => {
    if (isLoopScoped) return; // loop mode doesn't use this state
    const prevPrefilled = lastPrefilledBuildingRef.current;
    const userHasCustomSelection =
      selectedBuilding && selectedBuilding !== prevPrefilled;
    if (!userHasCustomSelection && prefilledBuilding !== prevPrefilled) {
      setSelectedBuilding(prefilledBuilding ?? null);
      lastPrefilledBuildingRef.current = prefilledBuilding ?? null;
    }
  }, [prefilledBuilding, isLoopScoped, selectedBuilding]);
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  const [selectedAcquisitionId, setSelectedAcquisitionId] =
    useState<string>("");
  const [availableAcquisitions, setAvailableAcquisitions] = useState<
    Acquisition[]
  >([]);
  const [loadingAcquisitions, setLoadingAcquisitions] = useState(false);

  // Fetch available labels
  const fetchLabels = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/file/search-labels?term=&resourceTypes=building,unit&documentTypes=picture`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch labels");

      const data = await response.json();
      setAvailableLabels(data);
      console.log(`📋 Fetched ${data.length} file labels for image picker`);
    } catch (error) {
      console.error("❌ Error fetching file labels:", error);
    }
  }, [session]);

  // Fetch labels on component mount (even when picker is hidden, for dropdown)
  useEffect(() => {
    if (session && isImagePickerVisible) {
      fetchLabels();
    }
  }, [session, isImagePickerVisible]);

  // Fetch available acquisitions
  const fetchAcquisitions = useCallback(async () => {
    try {
      setLoadingAcquisitions(true);
      const response = await fetchWithAuth(
        `${API_URL}/acquisitions`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch acquisitions");

      const data = await response.json();
      setAvailableAcquisitions(data);
      console.log(`📋 Fetched ${data.length} acquisitions for image picker`);
    } catch (error) {
      console.error("❌ Error fetching acquisitions:", error);
    } finally {
      setLoadingAcquisitions(false);
    }
  }, [session]);

  // Fetch acquisitions on component mount (even when picker is hidden, for dropdown)
  useEffect(() => {
    if (session && isImagePickerVisible) {
      fetchAcquisitions();
    }
  }, [session, isImagePickerVisible]);

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);

      // In loop-scoped mode the iteration's entity ids are authoritative — the
      // in-picker search bars and context toggle are hidden, so always trust
      // the props.
      const effectiveBuildingId = isLoopScoped
        ? buildingId || ""
        : selectedBuilding?.resourceId ||
          selectedBuilding?.id ||
          (useContextFilter ? buildingId : "");
      const effectiveUnitId = isLoopScoped
        ? unitId || ""
        : selectedUnit?.resourceId ||
          selectedUnit?.id ||
          (useContextFilter ? unitId : "");
      const effectiveAcquisitionId = isLoopScoped
        ? acquisitionId || ""
        : selectedAcquisitionId || (useContextFilter ? acquisitionId : "");

      console.log(`📸 ImagePicker fetching images for ${imageName}:`, {
        useContextFilter,
        selectedBuildingId:
          selectedBuilding?.resourceId || selectedBuilding?.id,
        selectedUnitId: selectedUnit?.resourceId || selectedUnit?.id,
        selectedAcquisitionId,
        buildingId: effectiveBuildingId,
        unitId: effectiveUnitId,
        acquisitionId: effectiveAcquisitionId,
      });

      const queryParams = new URLSearchParams({
        buildingId: effectiveBuildingId || "",
        unitId: effectiveUnitId || "",
        acquisitionId: effectiveAcquisitionId || "",
        exclusionType: "",
        fileLabel: selectedLabel || "",
        secondaryLabel: "",
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
      });

      const response = await fetchWithAuth(
        `${API_URL}/file/get-images?${queryParams}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            labels: [],
            buildingId: effectiveBuildingId,
            unitId: effectiveUnitId,
            acquisitionId: effectiveAcquisitionId,
          }),
        },
        session,
      );

      if (!response.ok) throw new Error("Failed to fetch images");

      const data = await response.json();
      const filteredImages = data.filter((img: FileLog) => img.imageFound);
      console.log(
        `📸 ImagePicker found ${filteredImages.length} images (page ${currentPage}) for ${imageName}`,
      );
      setImages(filteredImages);

      // Note: The API doesn't return total count, so we estimate based on page results
      // If we get a full page, there might be more images
      if (filteredImages.length === pageSize) {
        setTotalImages(currentPage * pageSize + 1); // At least one more page
      } else {
        setTotalImages((currentPage - 1) * pageSize + filteredImages.length);
      }
    } catch (error) {
      console.error(`❌ Error fetching images for ${imageName}:`, error);
    } finally {
      setLoading(false);
    }
  }, [
    session,
    buildingId,
    unitId,
    acquisitionId,
    currentPage,
    imageName,
    useContextFilter,
    selectedLabel,
    selectedBuilding,
    selectedUnit,
    selectedAcquisitionId,
    isLoopScoped,
  ]);

  useEffect(() => {
    if (session && isImagePickerVisible) {
      fetchImages();
    }
  }, [session, fetchImages, isImagePickerVisible]);

  const handleImageClick = (sharePointFileId: string) => {
    onImageSelect(sharePointFileId);
  };

  const handleClear = () => {
    onImageSelect("");
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <code className="rounded bg-purple-50 px-2 py-1 font-mono text-xs text-purple-700">
            {`{%${imageName}}`}
          </code>
          <Chip size="sm" color="secondary" variant="flat">
            Afbeelding
          </Chip>
          <Button
            size="sm"
            color="primary"
            variant="flat"
            onPress={() => setIsImagePickerVisible(!isImagePickerVisible)}
          >
            {isImagePickerVisible
              ? "Verberg afbeeldingen"
              : "Toon afbeeldingen"}
          </Button>
        </div>
        {selectedImageId && (
          <Button
            size="sm"
            color="danger"
            variant="light"
            startContent={<Trash size={14} />}
            onPress={handleClear}
          >
            Wissen
          </Button>
        )}
      </div>

      {isImagePickerVisible && (
        <>
          {/* Entity Selection and Filter Controls */}
          <Card>
            <CardBody className="space-y-3">
              {/* Entity Search Bars — hidden in loop mode, iteration already
                  determines the entity so the user should not be able to
                  override it. */}
              {!isLoopScoped && (
                <div className="space-y-3">
                  <EntitySearchBar
                    entityType="building"
                    entityInstance="imagePickerBuilding"
                    displayName="Gebouw"
                    selectedEntity={selectedBuilding}
                    onEntitySelect={(building) => {
                      setSelectedBuilding(building);
                      setCurrentPage(1);
                    }}
                    onEntityClear={() => {
                      setSelectedBuilding(null);
                      setCurrentPage(1);
                    }}
                  />

                  <EntitySearchBar
                    entityType="unit"
                    entityInstance="imagePickerUnit"
                    displayName="Unit"
                    selectedEntity={selectedUnit}
                    onEntitySelect={(unit) => {
                      setSelectedUnit(unit);
                      setCurrentPage(1);
                    }}
                    onEntityClear={() => {
                      setSelectedUnit(null);
                      setCurrentPage(1);
                    }}
                  />

                  {/* Acquisition Selector */}
                  <div className="space-y-2">
                    <label className="text-neutral-primaryDark block text-sm font-medium">
                      Acquisitie
                    </label>
                    <Select
                      size="sm"
                      placeholder="Selecteer acquisitie..."
                      selectedKeys={
                        selectedAcquisitionId ? [selectedAcquisitionId] : []
                      }
                      onSelectionChange={(keys) => {
                        const selected = Array.from(keys)[0] as string;
                        setSelectedAcquisitionId(selected || "");
                        setCurrentPage(1);
                      }}
                      isLoading={loadingAcquisitions}
                    >
                      {availableAcquisitions.map((acquisition) => (
                        <SelectItem key={acquisition.id?.toString()}>
                          {acquisition.name}
                        </SelectItem>
                      ))}
                    </Select>
                  </div>
                </div>
              )}

              {/* Label Filter */}
              {availableLabels.length > 0 && (
                <Select
                  size="sm"
                  label="Filter op label"
                  placeholder="Alle labels"
                  selectedKeys={selectedLabel ? [selectedLabel] : []}
                  onSelectionChange={(keys) => {
                    const selected = Array.from(keys)[0] as string;
                    setSelectedLabel(selected || "");
                    setCurrentPage(1); // Reset to first page on label change
                  }}
                  startContent={
                    <Tag size={16} className="text-neutral-secondaryMid" />
                  }
                  classNames={{
                    trigger: "h-10",
                  }}
                >
                  {availableLabels.map((label) => (
                    <SelectItem key={label.label}>{label.label}</SelectItem>
                  ))}
                </Select>
              )}

              {/* Context Filter Toggle — not shown in loop mode; iteration
                  context is always authoritative. */}
              {!isLoopScoped && (buildingId || unitId || acquisitionId) && (
                <div className="flex items-center justify-between">
                  <div className="text-neutral-secondaryMid text-xs">
                    {useContextFilter ? (
                      <span>Gefilterd op geselecteerde context</span>
                    ) : (
                      <span>Alle afbeeldingen doorzoeken</span>
                    )}
                  </div>
                  <Switch
                    size="sm"
                    isSelected={useContextFilter}
                    onValueChange={(checked) => {
                      setUseContextFilter(checked);
                      setCurrentPage(1); // Reset to first page on toggle
                    }}
                  >
                    <span className="text-xs">Gebruik context filter</span>
                  </Switch>
                </div>
              )}
            </CardBody>
          </Card>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="sm" label="Afbeeldingen laden..." />
            </div>
          ) : images.length === 0 ? (
            <Card>
              <CardBody className="py-8 text-center">
                <ImageIcon
                  size={32}
                  className="text-neutral-secondaryMid mx-auto mb-2"
                />
                <p className="text-neutral-secondaryMid text-sm">
                  Geen afbeeldingen gevonden
                </p>
              </CardBody>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-2">
                {images.map((image) => (
                  <button
                    key={image.id}
                    onClick={() => handleImageClick(image.sharePointFileId)}
                    className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all hover:scale-105 ${
                      selectedImageId === image.sharePointFileId
                        ? "border-primary shadow-lg"
                        : "border-neutral-border hover:border-primary"
                    }`}
                  >
                    <img
                      src={image.imageUrl}
                      alt={image.fileName}
                      className="h-full w-full object-cover"
                    />
                    {selectedImageId === image.sharePointFileId && (
                      <div className="bg-primary bg-opacity-20 absolute inset-0 flex items-center justify-center">
                        <div className="bg-primary rounded-full p-2">
                          <Check
                            size={20}
                            weight="bold"
                            className="text-white"
                          />
                        </div>
                      </div>
                    )}
                  </button>
                ))}
              </div>

              {/* Pagination Controls */}
              {totalImages > pageSize && (
                <div className="border-neutral-border flex items-center justify-between border-t pt-2">
                  <Button
                    size="sm"
                    variant="flat"
                    isIconOnly
                    isDisabled={currentPage === 1}
                    onPress={() =>
                      setCurrentPage((prev) => Math.max(1, prev - 1))
                    }
                  >
                    <CaretLeft size={16} weight="bold" />
                  </Button>

                  <div className="flex items-center gap-2">
                    <span className="text-neutral-secondaryMid text-xs">
                      Pagina {currentPage}
                    </span>
                    <Chip size="sm" variant="flat">
                      {totalImages > currentPage * pageSize
                        ? `${images.length}+ afbeeldingen`
                        : `${totalImages} afbeeldingen`}
                    </Chip>
                  </div>

                  <Button
                    size="sm"
                    variant="flat"
                    isIconOnly
                    isDisabled={images.length < pageSize}
                    onPress={() => setCurrentPage((prev) => prev + 1)}
                  >
                    <CaretRight size={16} weight="bold" />
                  </Button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {!isImagePickerVisible && selectedImageId && (
        <div className="text-neutral-secondary-mid rounded bg-neutral-50 px-2 py-2 text-sm">
          Afbeelding geselecteerd voor {`{%${imageName}}`}
        </div>
      )}

      {selectedImageId && (
        <div className="text-success flex items-center gap-1 text-xs">
          <Check size={14} weight="bold" />
          <span>Afbeelding geselecteerd</span>
        </div>
      )}
    </div>
  );
};

export default ImagePicker;
